import styled, { createGlobalStyle } from 'styled-components';
import { Button, Input, Select, Table, Tabs, Modal } from 'antd';
import { COLORS } from '../../constants/colors';

export const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  margin-bottom: 16px;
`;

export const PrimaryButton = styled(Button)`
  height: 40px;
  border-radius: 10px;
  font-weight: 600;
  padding: 0 20px;
  background: ${COLORS.$primaryBlue};
  border-color: ${COLORS.$primaryBlue};
  transition: .2s ease;
  &:hover,
  &:focus {
    background: ${COLORS.$primaryBlueHover};
    border-color: ${COLORS.$primaryBlueHover};
    box-shadow: 0 4px 10px rgba(24,144,255,.25);
  }
`;

export const CountPill = styled.span`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${COLORS.$primaryBlue};
  background: rgba(24, 144, 255, 0.10);
  padding: 2px 8px;
  border-radius: 999px;
  min-width: 20px;
  text-align: center;
`;

export const PageContainer = styled.div`
  
  /* Animation Keyframes */
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* Header Animations - Faster and more subtle */
  .header-animate-in {
    animation: fadeInDown 0.3s ease-out forwards;
    opacity: 1; /* Start visible for better UX */
  }
  
  .header-animate-out {
    opacity: 1; /* Keep visible */
  }
  
  /* Filters Animations - Faster and more subtle */
  .filters-animate-in {
    animation: slideInLeft 0.3s ease-out forwards;
    opacity: 1; /* Start visible for better UX */
  }
  
  .filters-animate-out {
    opacity: 1; /* Keep visible */
  }
  
  /* Table Animations - Faster and more subtle */
  .table-animate-in {
    animation: fadeInUp 0.3s ease-out forwards;
    opacity: 1; /* Start visible for better UX */
  }
  
  .table-animate-out {
    opacity: 1; /* Keep visible */
  }
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 2px solid ${COLORS.border};
  
  /* Full-width border extending to edges */
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 24px;
  padding-right: 24px;
  
  @media (max-width: 768px) {
    margin-left: -20px;
    margin-right: -20px;
    padding-left: 20px;
    padding-right: 20px;
  }
  
  /* Animation support */
  animation-fill-mode: both;
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: ${COLORS.textDark};
  font-size: 24px;  
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ActionButton = styled(Button)`
  height: 48px !important;
  border-radius: 8px !important;
  font-weight: 600;
  padding: 0 20px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
  
  &.ant-btn-primary {
    background: ${COLORS.$primaryBlue} !important;
    border: none !important;
    color: white !important;
    
    &:hover,
    &:focus {
      background: ${COLORS.$primaryBlueHover} !important;
      border: none !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3) !important;
      transform: translateY(-1px);
    }
    
    &:active {
      background: ${COLORS.$primaryBlueDark} !important;
      box-shadow: none !important;
    }
  }
  
  &:not(.ant-btn-primary) {
    border: 1px solid #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.65);
    
    &:hover {
      background: #fafafa;
      color: rgba(0, 0, 0, 0.88);
      border-color: #d9d9d9;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
    }
  }
  
  &.edit-btn {
    color: ${COLORS.$primaryBlue};
    border-color: ${COLORS.$primaryBlue};
    height: 32px !important;
    padding: 0 12px !important;
    font-size: 12px !important;
    
    &:hover {
      background: ${COLORS.$primaryBlue};
      color: ${COLORS.white};
      box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3) !important;
    }
  }
  
  &.reset-btn {
    color: ${COLORS.warningAmber};
    border-color: ${COLORS.warningAmber};
    height: 32px !important;
    padding: 0 12px !important;
    font-size: 12px !important;
    
    &:hover {
      background: ${COLORS.warningAmber};
      color: ${COLORS.white};
      box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3) !important;
    }
  }
  
  &.delete-btn {
    color: ${COLORS.criticalRed};
    border-color: ${COLORS.criticalRed};
    height: 32px !important;
    padding: 0 12px !important;
    font-size: 12px !important;
    
    &:hover {
      background: ${COLORS.criticalRed};
      color: ${COLORS.white};
      box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3) !important;
    }
  }
`;

export const FilterContainer = styled.div`
  background: ${COLORS.gradient.subtleGray};
  padding: 28px;
  border-radius: 16px;
  margin-bottom: 32px;
  border: 1px solid ${COLORS.border};
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  
  /* Animation support */
  animation-fill-mode: both;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }
  
  /* Responsive filter layout */
  .filter-row {
    display: flex;
    align-items: flex-end;
    gap: 20px;
    width: 100%;
    flex-wrap: wrap;
    
    .search-section {
      flex: 1;
      min-width: 300px;
    }
    
    .filter-section {
      min-width: 140px;
    }
      @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
      
      .search-section {
        min-width: 100%;
      }
      
      .filter-section {
        min-width: 100%;
        
        .ant-select {
          width: 100% !important;
        }
      }
    }
    
    @media (max-width: 576px) {
      gap: 12px;
      
      .filter-section {
        .ant-select {
          height: 48px !important;
          
          .ant-select-selector {
            height: 48px !important;
            
            .ant-select-selection-item,
            .ant-select-selection-placeholder {
              line-height: 46px !important;
              height: 46px !important;
            }
          }
        }
      }
    }
  }
`;

export const StyledInput = styled(Input)`
  height: 48px !important;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  box-shadow: none !important;
  font-size: 16px !important;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: none !important;
  }
  
  &:focus,
  &.ant-input-focused {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-input-prefix {
    margin-right: 8px;
    
    .anticon {
      color: ${COLORS.textMuted};
      font-size: 16px;
    }
  }
`;

export const StyledSelect = styled(Select)`
  height: 48px !important;
  width: 100% !important;
  
  .ant-select-selector {
    height: 48px !important;
    border-radius: 8px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: none !important;
    font-size: 16px !important;
    padding: 0 11px !important;
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    transition: all 0.2s ease;
    background: white !important;
    
    &:hover {
      border-color: ${COLORS.$primaryBlue} !important;
      box-shadow: none !important;
    }
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-select-selection-item {
    line-height: 46px !important;
    height: 46px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  
  .ant-select-selection-placeholder {
    line-height: 46px !important;
    height: 46px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    color: ${COLORS.textMuted} !important;
  }
  
  .ant-select-arrow {
    color: ${COLORS.textMuted} !important;
    font-size: 14px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: 11px !important;
    height: 14px !important;
    margin-top: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: all 0.2s ease;
  }
  
  &:hover .ant-select-arrow {
    color: ${COLORS.$primaryBlue} !important;
  }
  
  /* Custom dropdown styling to match screening page */
  .ant-select-dropdown {
    border-radius: 8px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
    padding: 4px 0 !important;
    
    .ant-select-item {
      padding: 8px 12px !important;
      font-size: 14px !important;
      font-weight: 500 !important;
      color: ${COLORS.textDark} !important;
      transition: all 0.2s ease !important;
      
      &:hover {
        background: rgba(24, 144, 255, 0.08) !important;
        color: ${COLORS.$primaryBlue} !important;
      }
      
      &.ant-select-item-option-selected {
        background: ${COLORS.$primaryBlue} !important;
        color: white !important;
        font-weight: 600 !important;
      }
    }
  }
`;

export const StyledTable = styled(Table)`
  /* Animation support */
  animation-fill-mode: both;
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    
    /* Alternating row colors for better readability */
    &:nth-child(even) {
      background: #fafafa;
      
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }
  
  /* Pagination styling - matching EventManagement */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        height: 32px !important;
        
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 28px 0 12px !important;
          box-sizing: border-box !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        .ant-select-selection-item {
          height: 28px !important;
          line-height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          flex: 1 !important;
          min-width: 0 !important;
        }
        
        .ant-select-selection-search {
          height: 28px !important;
          padding-left: 8px !important;
          
          .ant-select-selection-search-input {
            height: 28px !important;
            line-height: 28px !important;
          }
        }
        
        .ant-select-arrow {
          right: 10px !important;
          flex-shrink: 0 !important;
          height: 14px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          
          .anticon {
            line-height: 1 !important;
            vertical-align: middle !important;
          }
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
        
        /* Dropdown menu styling */
        .ant-select-dropdown {
          border-radius: 10px !important;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15) !important;
          border: 1px solid ${COLORS.border} !important;
          
          .ant-select-item {
            padding: 8px 12px !important;
            font-weight: 500 !important;
            color: ${COLORS.textDark} !important;
            transition: all 0.2s ease !important;
            min-height: 32px !important;
            font-size: 13px !important;
            line-height: 1.4 !important;
            display: flex !important;
            align-items: center !important;
            
            &:hover {
              background: rgba(24, 144, 255, 0.1) !important;
              color: ${COLORS.$primaryBlue} !important;
            }
            
            &.ant-select-item-option-selected {
              background: ${COLORS.$primaryBlue} !important;
              color: white !important;
              font-weight: 600 !important;
              
              .ant-select-item-option-state {
                color: white !important;
              }
              
              &:hover {
                background: ${COLORS.$primaryBlueDark} !important;
                color: white !important;
              }
            }
            
            .ant-select-item-option-state {
              color: ${COLORS.$primaryBlue} !important;
              
              .anticon {
                font-weight: bold !important;
              }
            }
          }
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        font-size: 13px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s ease !important;
          line-height: 28px !important;
          padding: 0 0 0 8px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
  
  /* Dropdown menu styling for pagination */
  .ant-pagination .ant-select-dropdown,
  .ant-pagination-options-size-changer .ant-select-dropdown {
    border-radius: 10px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
    padding: 4px !important;
    
    .ant-select-item {
      border-radius: 8px !important;
      margin: 2px 0 !important;
      padding: 8px 12px !important;
      color: ${COLORS.textDark} !important;
      min-height: 32px !important;
      font-size: 13px !important;
      line-height: 1.4 !important;
      display: flex !important;
      align-items: center !important;
      transform: none !important;
      box-shadow: none !important;
      
      &:hover {
        background-color: rgba(24, 144, 255, 0.08) !important;
        color: ${COLORS.$primaryBlue} !important;
        transform: none !important;
        box-shadow: none !important;
      }
      
      &.ant-select-item-option-selected {
        background-color: ${COLORS.$primaryBlue} !important;
        color: white !important;
        font-weight: 600 !important;
        
        .ant-select-item-option-state {
          color: white !important;
        }
        
        &::after {
          display: none !important;
        }
      }
    }
    
    .ant-select-item-option-state {
      color: ${COLORS.$primaryBlue} !important;
      font-weight: bold !important;
    }
  }
  
  /* Custom pagination class for better control */
  &.user-management-pagination {
    .ant-pagination {
      justify-content: space-between;
      
      @media (max-width: 768px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between; /* Left/right alignment */
        gap: 8px;
        flex-wrap: nowrap; /* Single row */
        
        /* Hide total text for compact mobile layout */
        .ant-pagination-total-text {
          display: none !important;
        }
        
        .ant-pagination-options {
          margin-left: auto; /* Push to right side */
          order: 2; /* Ensure it comes after pagination items */
          
          .ant-pagination-options-size-changer {
            .ant-select-selector {
              height: 33px !important; /* Updated height as requested */
              min-height: 33px !important;
              font-size: 12px !important;
              padding: 0 8px !important;
            }
          }
        }
        
        /* Create a compact container for pagination buttons - LEFT SIDE */
        .ant-pagination-item,
        .ant-pagination-prev,
        .ant-pagination-next,
        .ant-pagination-jump-prev,
        .ant-pagination-jump-next {
          flex: 0 0 auto !important;
          width: auto !important;
          min-width: 32px !important;
          max-width: 36px !important;
          height: 33px !important; /* Updated height as requested */
          margin: 0 1px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 13px !important;
          line-height: 31px !important; /* Adjusted for new height */
          order: 1; /* Ensure they come before page size selector */
        }
      }
    }
  }

  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 16px 12px;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    
    /* Tags - make them smaller */
    .ant-tag {
      font-size: 10px !important;
      padding: 2px 6px !important;
    }
  }

  @media (max-width: 576px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
      font-size: 11px;
    }
    
    .ant-table-thead > tr > th {
      height: 44px !important;
      font-size: 10px !important;
    }
    
    /* Very small tags */
    .ant-tag {
      font-size: 9px !important;
      padding: 1px 4px !important;
    }
  }
`;

export const PaginationText = styled.span`
  color: ${COLORS.textMedium};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.2px;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const StyledTableContainer = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid ${COLORS.border};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  /* Animation support */
  animation-fill-mode: both;
`;

/* Global styles for all Select dropdowns */
export const GlobalSelectStyles = createGlobalStyle`
  /* Global arrow positioning to match Screening data page */
  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: 11px !important;
    height: 14px !important;
    width: 14px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    color: ${COLORS.textMuted} !important;
    font-size: 14px !important;
    line-height: 1 !important;
    margin-top: 0 !important;
    
    .anticon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;
      height: 14px !important;
      width: 14px !important;
    }
  }

  .ant-select-dropdown {
    border-radius: 8px !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
    border: 1px solid ${COLORS.border} !important;
    padding: 6px 0 !important;
    z-index: 1050 !important;
    background: white !important;
    overflow: hidden !important;
  }

  .ant-select-item {
    padding: 10px 16px !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
    font-size: 14px !important;
    line-height: 1.4 !important;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
    color: #333 !important;
    margin: 0 4px !important;
    border-radius: 4px !important;
    cursor: pointer !important;
    
    &:hover {
      background-color: ${COLORS.$primaryBlue} !important;
      color: white !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2) !important;
    }
    
    &.ant-select-item-option-selected {
      background-color: ${COLORS.$primaryBlue} !important;
      color: white !important;
      font-weight: 500 !important;
      
      &::after {
        content: "✓" !important;
        margin-left: auto !important;
        font-weight: bold !important;
        color: white !important;
      }
    }
    
    &.ant-select-item-option-active {
      background-color: rgba(24, 144, 255, 0.1) !important;
      color: ${COLORS.$primaryBlue} !important;
      border: 1px solid ${COLORS.$primaryBlue} !important;
    }

    &.ant-select-item-option-selected.ant-select-item-option-active {
      background-color: ${COLORS.$primaryBlue} !important;
      color: white !important;
      border: 1px solid transparent !important;
    }
  }

  .ant-select-item-option-content {
    flex: 1;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  .ant-select-item-empty {
    color: #999 !important;
    text-align: center !important;
    font-style: italic !important;
    padding: 16px !important;
  }

  .ant-select-item-group {
    padding: 8px 16px 4px !important;
    font-size: 12px !important;
    color: #999 !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
  }
`;

export const StyledTabs = styled(Tabs)`
  margin: 0 !important;
  background: transparent;
  padding-top: 16px;
  padding-bottom: 8px;
  margin-bottom: 0;
  
  .ant-tabs-nav {
    margin: 0 !important;
    
    &::before {
      display: none !important;
    }
  }
  
  /* Hide ink bar */
  .ant-tabs-ink-bar {
    display: none !important;
  }
  
  /* Make the tab strip horizontally scrollable on small widths */
  .ant-tabs-nav-wrap {
    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    
    &::-webkit-scrollbar {
      display: none; /* WebKit */
    }
  }
  
  .ant-tabs-nav-list {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    
    .ant-tabs-tab {
      position: relative;
      padding: 0;
      margin: 0;
      background: transparent;
      border: none;
      transition: all 0.2s ease;
      flex: none;
      width: auto;
      
      /* Remove dividers */
      &::before {
        display: none;
      }
      
      .ant-tabs-tab-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        border-radius: 999px;
        color: ${COLORS.textMedium};
        font-weight: 500;
        font-size: 14px;
        transition: background-color .2s ease, color .2s ease;
        background: transparent;
        border: none;
        min-height: 40px;
        white-space: nowrap;
        
        .anticon {
          font-size: 16px;
        }
      }
      
      &:hover .ant-tabs-tab-btn {
        color: ${COLORS.textDark};
        background: rgba(24, 144, 255, 0.08);
      }
      
      &.ant-tabs-tab-active .ant-tabs-tab-btn {
        color: ${COLORS.$primaryBlue};
        background: #eef4ff;
        font-weight: 600;
        box-shadow: 0 1px 3px rgba(0,0,0,.06);
        border: 1px solid rgba(24, 144, 255, 0.3);
      }
      
      /* Visible keyboard focus */
      .ant-tabs-tab-btn:focus-visible {
        outline: 2px solid ${COLORS.$primaryBlue};
        outline-offset: 2px;
        border-radius: 999px;
      }
    }
  }
  
  /* If header-only tabs (no inner tab panels) */
  .ant-tabs-content-holder {
    display: none;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .ant-tabs-nav-list {
      gap: 2px;
      
      .ant-tabs-tab {
        .ant-tabs-tab-btn {
          font-size: 13px;
          padding: 8px 12px;
          min-height: 36px;
          
          .anticon {
            font-size: 14px;
          }
        }
      }
    }
  }
  
  @media (max-width: 576px) {
    .ant-tabs-nav-list {
      .ant-tabs-tab {
        .ant-tabs-tab-btn {
          font-size: 12px;
          padding: 6px 8px;
          min-height: 32px;
          gap: 4px;
          
          .anticon {
            font-size: 12px;
          }
        }
      }
    }
  }
`;

export const TabsBorderDiv = styled.div`
  border-bottom: 1px solid ${COLORS.border};
  margin-bottom: 20px;
`;

export const ResponsiveModal = styled(Modal)`
  /* Modal positioning is handled by global CSS in modalResponsive.css */
  /* This component can be used for additional modal-specific styling */
`;

export const SignaturePreview = styled.div`
  max-width: 400px;
  margin: 16px 0;
  
  img {
    max-width: 100%;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: white;
    padding: 8px;
  }
  
  .typed-signature {
    font-family: 'Brush Script MT', cursive;
    font-size: 32px;
    color: #1a365d;
    padding: 16px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: white;
    text-align: center;
  }
`;

export const AcknowledgmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

export const AcknowledgmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  .icon {
    font-size: 16px;
    color: ${props => props.$acknowledged ? '#52c41a' : '#ff4d4f'};
  }
`;

export const WaiverEventCard = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border-left: 4px solid ${props => props.$isLatest ? '#52c41a' : '#e8e8e8'};
`;
